'use client'

import { useMemo } from 'react'
import { useInfiniteProductQuestionsQuery } from '@/generated/graphql'
import { Question } from '../Question'
import { Button } from '@/General/Button'
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from '@/General/LoadingSpinner'

const QUESTIONS_PER_PAGE = 10

export const QuestionsListClient = ({
  initialQuestions,
  productId,
  productUrl,
  hasNextPage: initialHasNextPage,
}: QuestionsListClientProps) => {
  // Determine if we should start from page 1 or page 2
  // If we have initial questions from SSR, start from page 2, otherwise start from page 1
  const hasInitialQuestions = initialQuestions && initialQuestions.length > 0
  const initialSkip = hasInitialQuestions ? QUESTIONS_PER_PAGE : 0

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isError,
    error,
  } = useInfiniteProductQuestionsQuery(
    {
      productId,
      take: QUESTIONS_PER_PAGE,
      skip: 0, // Default value, will be overridden by pageParam
    },
    {
      // Start from page 2 if we have initial questions, otherwise start from page 1
      initialPageParam: { skip: initialSkip },
      getNextPageParam: (lastPage, allPages) => {
        const hasNext =
          lastPage?.productQuestions?.pageInfo?.hasNextPage
        if (!hasNext) return undefined

        // Calculate total items loaded across all pages
        const totalLoadedFromPages = allPages.reduce(
          (total, page) =>
            total + (page?.productQuestions?.items?.length || 0),
          0
        )

        // If we started from page 2, we need to account for the initial questions
        const totalLoaded = hasInitialQuestions
          ? (initialQuestions?.length || 0) + totalLoadedFromPages
          : totalLoadedFromPages

        return { skip: totalLoaded }
      },
      getPreviousPageParam: () => undefined,
      // Always enable the query
      enabled: true,
      // If we don't have initial questions, we need to fetch the first page
      // The infinite query will automatically fetch with initialPageParam
    }
  )

  // Flatten all pages into a single array of questions
  const allQuestions = useMemo(() => {
    const questionsFromPages =
      data?.pages
        ?.flatMap(
          (page) => page?.productQuestions?.items || []
        )
        .filter(Boolean) || []

    // If we have initial questions, combine and avoid duplicates
    if (hasInitialQuestions) {
      const initialIds = new Set(
        (initialQuestions || []).map((q) => q.id)
      )
      const newQuestions = questionsFromPages.filter(
        (q) => !initialIds.has(q.id)
      )
      return [...(initialQuestions || []), ...newQuestions]
    }

    // If no initial questions, just return what we fetched
    return questionsFromPages
  }, [data?.pages, initialQuestions, hasInitialQuestions])

  const hasMore = hasNextPage ?? initialHasNextPage ?? false
  const communityBaseUrl = productUrl.replace(/\/+$/, '')
  const returnUrlParam = encodeURIComponent(communityBaseUrl)

  return (
    <div id="questions-section" className="mx-0">
      <ul className="space-y-1 break-words">
        {allQuestions.map(question => (
          <Question
            key={question.id}
            id={question.id.toString()}
            customerName={question.customerName || ''}
            question={question.question || ''}
            answers={
              question.answers?.map(answer => ({
                id: answer.id.toString(),
                customerId: answer.customerId,
                customerName: answer.customerName || '',
                answer: answer.answer || '',
                isCreator: answer.isCreator || false,
              })) || []
            }
            productUrl={communityBaseUrl}
          />
        ))}
      </ul>

      {isError && (
        <div className="flex justify-center py-4">
          <p className="text-red-600">
            Error loading questions: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      )}

      {(isFetching || isFetchingNextPage) && (
        <div className="flex justify-center py-4">
          <LoadingSpinner />
        </div>
      )}

      <div className="flex justify-between items-center my-4">
        <div>
          {allQuestions.length > 0 && (
            <p className="text-sm text-gray-600">
              {allQuestions.length} question{allQuestions.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <div className="flex gap-4">
          {hasMore && !isFetchingNextPage && (
            <Button onClick={() => fetchNextPage()} color="blue" outline>
              Load more questions
            </Button>
          )}
          <Button
            href={`${communityBaseUrl}/communityquestion?returnUrl=${returnUrlParam}`}
            color="blue"
          >
            <QuestionMarkCircleIcon className="h-5 w-5" data-slot="icon" />
            Ask a question
          </Button>
        </div>
      </div>
    </div>
  )
}

interface QuestionsListClientProps {
  initialQuestions: Array<{
    id: number
    customerName?: string | null
    question?: string | null
    answers?: Array<{
      id: number
      customerId: number
      customerName?: string | null
      answer?: string | null
      isCreator?: boolean | null
    }> | null
  }> | null
  productId: number
  productUrl: string
  hasNextPage?: boolean
}
